import type { KeyChord } from "../services/commands/commands.types";

/** How a key is drawn on a keycap, and how a screen reader says it. */
const KEY_NAMES: Record<string, { cap: string; spoken: string }> = {
  arrowup: { cap: "↑", spoken: "Up arrow" },
  arrowdown: { cap: "↓", spoken: "Down arrow" },
  arrowleft: { cap: "←", spoken: "Left arrow" },
  arrowright: { cap: "→", spoken: "Right arrow" },
  enter: { cap: "Enter", spoken: "Enter" },
  escape: { cap: "Esc", spoken: "Escape" },
  backspace: { cap: "Backspace", spoken: "Backspace" },
  delete: { cap: "Del", spoken: "Delete" },
  tab: { cap: "Tab", spoken: "Tab" },
  home: { cap: "Home", spoken: "Home" },
  end: { cap: "End", spoken: "End" },
  pageup: { cap: "PgUp", spoken: "Page up" },
  pagedown: { cap: "PgDn", spoken: "Page down" },
  " ": { cap: "Space", spoken: "Space" },
  contextmenu: { cap: "Menu", spoken: "Menu key" },
};

export interface KeyCap {
  /** What the keycap shows. */
  cap: string;
  /** What a screen reader says. */
  spoken: string;
}

const MODIFIERS = {
  mac: {
    ctrl: { cap: "⌃", spoken: "Control" },
    alt: { cap: "⌥", spoken: "Option" },
    shift: { cap: "⇧", spoken: "Shift" },
    mod: { cap: "⌘", spoken: "Command" },
  },
  other: {
    ctrl: { cap: "Ctrl", spoken: "Control" },
    alt: { cap: "Alt", spoken: "Alt" },
    shift: { cap: "Shift", spoken: "Shift" },
    mod: { cap: "Ctrl", spoken: "Control" },
  },
} as const;

/** The keycaps of one chord, in display order (macOS: ⌃ ⌥ ⇧ ⌘ key; elsewhere: Ctrl Alt Shift key). */
export function formatKeyChord(chord: KeyChord, mac: boolean): KeyCap[] {
  const mods = mac ? MODIFIERS.mac : MODIFIERS.other;
  const caps: KeyCap[] = [];
  // Off macOS, Mod and Ctrl are the same physical key: show it once.
  if (chord.ctrl || (!mac && chord.mod)) caps.push(mods.ctrl);
  if (chord.alt) caps.push(mods.alt);
  if (chord.shift) caps.push(mods.shift);
  if (mac && chord.mod) caps.push(mods.mod);
  const named = KEY_NAMES[chord.key.toLowerCase()];
  caps.push(named ?? { cap: chord.key.length === 1 ? chord.key.toUpperCase() : chord.key, spoken: chord.key.length === 1 ? chord.key.toUpperCase() : chord.key });
  return caps;
}

/** A chord as one phrase for `aria-label`: "Command K", "Shift Control Z". */
export function spokenKeyChord(chord: KeyChord, mac: boolean): string {
  return formatKeyChord(chord, mac)
    .map((cap) => cap.spoken)
    .join(" ");
}
